const API_BASE = import.meta.env.DEV ? '' : ''

interface ApiResponse<T> {
    data?: T
    error?: string
}

/**
 * Generic API call function with error handling
 */
export async function apiCall<T = any>(
    endpoint: string,
    options?: RequestInit
): Promise<ApiResponse<T>> {
    try {
        const response = await fetch(`${API_BASE}${endpoint}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options?.headers,
            },
            credentials: 'include',
        })

        const data = await response.json()

        if (!response.ok) {
            return {
                error: data.error || data.message || 'An error occurred',
            }
        }

        return { data }
    } catch (error) {
        return {
            error: error instanceof Error ? error.message : 'Network error',
        }
    }
}

// ============================================
// AUTH API
// ============================================

export const authApi = {
    login: (data: { email: string; password: string; turnstileToken?: string }) =>
        apiCall<{ user: any; token: string }>('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    register: (data: {
        email: string
        password: string
        fullName: string
        role: 'customer' | 'owner'
        turnstileToken?: string
    }) =>
        apiCall<{ user: any; token: string }>('/api/auth/register', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    logout: () => apiCall('/api/auth/logout', { method: 'POST' }),

    me: () => apiCall<{ user: any }>('/api/auth/me'),

    googleLogin: () => {
        window.location.href = '/api/auth/google/login'
    },
}

// ============================================
// VEHICLES API
// ============================================

export const vehiclesApi = {
    list: (params?: {
        type?: string
        minPrice?: number
        maxPrice?: number
        location?: string
        page?: number
        limit?: number
    }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{
            vehicles: any[]
            total: number
            page: number
            totalPages: number
        }>(`/api/vehicles?${searchParams}`)
    },

    get: (id: number) => apiCall<any>(`/api/vehicles/${id}`),

    create: (data: FormData) =>
        apiCall('/api/vehicles', {
            method: 'POST',
            body: data,
            headers: {}, // Let browser set content-type for FormData
        }),

    update: (id: number, data: FormData) =>
        apiCall(`/api/vehicles/${id}`, {
            method: 'PUT',
            body: data,
            headers: {},
        }),

    delete: (id: number) =>
        apiCall(`/api/vehicles/${id}`, { method: 'DELETE' }),

    getAvailability: (id: number, month: string) =>
        apiCall<{ dates: string[] }>(`/api/vehicles/${id}/availability?month=${month}`),

    // Admin methods
    adminList: (params?: { type?: string; status?: string; search?: string }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ success: boolean; data: any[]; stats: any }>(`/api/admin/vehicles?${searchParams}`)
    },

    getById: (id: number) => apiCall<{ success: boolean; data: any }>(`/api/admin/vehicles/${id}`),

    adminCreate: (data: any) => apiCall<{ success: boolean; id?: number }>('/api/admin/vehicles', {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    adminUpdate: (id: number, data: any) => apiCall<{ success: boolean }>(`/api/admin/vehicles?id=${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    adminDelete: (id: number) => apiCall<{ success: boolean }>(`/api/admin/vehicles?id=${id}`, { method: 'DELETE' }),
}

// ============================================
// BOOKINGS API
// ============================================

export const bookingsApi = {
    create: (data: {
        vehicleId: number
        startDate: string
        endDate: string
        pickupLocation: string
        addons: number[]
    }) =>
        apiCall<{ booking: any }>('/api/bookings', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    list: (params?: { status?: string; page?: number; limit?: number }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ bookings: any[]; total: number }>(`/api/bookings?${searchParams}`)
    },

    get: (id: number) => apiCall<any>(`/api/bookings/${id}`),

    confirm: (id: number) =>
        apiCall(`/api/bookings/${id}/confirm`, { method: 'POST' }),

    cancel: (id: number, reason: string) =>
        apiCall(`/api/bookings/${id}/cancel`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
        }),

    complete: (id: number) =>
        apiCall(`/api/bookings/${id}/complete`, { method: 'POST' }),
}

// ============================================
// WALLET API
// ============================================

export const walletApi = {
    getBalance: () => apiCall<{ balance: number; currency: string }>('/api/wallet'),

    getTransactions: (page?: number) =>
        apiCall<{ transactions: any[]; total: number }>(`/api/wallet/transactions?page=${page || 1}`),

    requestWithdrawal: (data: { amount: number; bankInfo: any }) =>
        apiCall('/api/wallet/withdraw', {
            method: 'POST',
            body: JSON.stringify(data),
        }),
}

// ============================================
// TOURS API
// ============================================

export const toursApi = {
    list: (params?: { category?: string; page?: number; limit?: number }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ tours: any[]; total: number }>(`/api/tours?${searchParams}`)
    },

    get: (id: number) => apiCall<any>(`/api/tours/${id}`),

    // Admin methods
    adminList: (params?: { type?: string; status?: string }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ success: boolean; data: any[]; stats: any }>(`/api/admin/tours?${searchParams}`)
    },

    getById: (id: number) => apiCall<{ success: boolean; data: any }>(`/api/admin/tours/${id}`),

    create: (data: any) => apiCall<{ success: boolean; id?: number }>('/api/admin/tours', {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    update: (id: number, data: any) => apiCall<{ success: boolean }>(`/api/admin/tours?id=${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    delete: (id: number) => apiCall<{ success: boolean }>(`/api/admin/tours?id=${id}`, { method: 'DELETE' }),
}

// ============================================
// NEWS API
// ============================================

export const newsApi = {
    list: (params?: { category?: string; location?: string; page?: number; limit?: number }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ success: boolean; data: any[]; total: number }>(`/api/news?${searchParams}`)
    },

    get: (slug: string) => apiCall<{ success: boolean; data: any }>(`/api/news/${slug}`),

    getById: (id: number) => apiCall<{ success: boolean; data: any }>(`/api/news/id/${id}`),

    create: (data: any) => apiCall<{ success: boolean; id?: number }>('/api/news', {
        method: 'POST',
        body: JSON.stringify(data),
    }),

    update: (id: number, data: any) => apiCall<{ success: boolean }>(`/api/news/id/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    }),

    delete: (id: number) => apiCall<{ success: boolean }>(`/api/admin/news/${id}`, { method: 'DELETE' }),

    publish: (id: number) => apiCall<{ success: boolean }>(`/api/admin/news/${id}/publish`, { method: 'POST' }),

    unpublish: (id: number) => apiCall<{ success: boolean }>(`/api/admin/news/${id}/unpublish`, { method: 'POST' }),
}

// ============================================
// ADMIN API
// ============================================

export const adminApi = {
    // Withdrawals
    getWithdrawals: (status?: string) =>
        apiCall<{ withdrawals: any[] }>(`/api/admin/withdrawals?status=${status || 'pending'}`),

    approveWithdrawal: (id: number) =>
        apiCall(`/api/admin/withdrawals/${id}/approve`, { method: 'POST' }),

    rejectWithdrawal: (id: number, reason: string) =>
        apiCall(`/api/admin/withdrawals/${id}/reject`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
        }),

    // Users
    getUsers: (params?: { role?: string; search?: string; verified?: boolean }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ data: any[]; stats: any }>(`/api/admin/users?${searchParams}`)
    },

    updateUser: (id: number, data: any) =>
        apiCall(`/api/admin/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    verifyUser: (id: number) =>
        apiCall(`/api/admin/users/${id}/verify`, { method: 'POST' }),

    deleteUser: (id: number) =>
        apiCall(`/api/admin/users/${id}`, { method: 'DELETE' }),

    // Vehicles
    getVehicles: (params?: { status?: string; type?: string; search?: string }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ data: any[]; stats: any }>(`/api/admin/vehicles?${searchParams}`)
    },

    approveVehicle: (id: number) =>
        apiCall(`/api/admin/vehicles/${id}/approve`, { method: 'POST' }),

    rejectVehicle: (id: number) =>
        apiCall(`/api/admin/vehicles/${id}/reject`, { method: 'POST' }),

    deleteVehicle: (id: number) =>
        apiCall(`/api/admin/vehicles/${id}`, { method: 'DELETE' }),

    // Bookings
    getBookings: (params?: { status?: string; search?: string }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ data: any[]; stats: any }>(`/api/admin/bookings?${searchParams}`)
    },

    updateBookingStatus: (id: number, status: string) =>
        apiCall(`/api/bookings/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
        }),

    // News
    getNews: (params?: { status?: string; category?: string }) => {
        const searchParams = new URLSearchParams()
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined) searchParams.append(key, String(value))
            })
        }
        return apiCall<{ data: any[]; total: number }>(`/api/news?${searchParams}`)
    },

    createNews: (data: any) =>
        apiCall('/api/news', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    updateNews: (slug: string, data: any) =>
        apiCall(`/api/news/${slug}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    deleteNews: (slug: string) =>
        apiCall(`/api/news/${slug}`, { method: 'DELETE' }),

    // Dashboard Stats
    getStats: () => apiCall<any>('/api/admin/stats'),
}

